import React, { FC, useMemo } from 'react';

import {
	EvFilters,
	EvSection,
	IFilterValues,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY
} from '@evinced-private/ui-common';

import { MFA_SESSION_FILTERS_SECTION_ID } from 'data-automation-ids/MfaDashboardAutomationIds';
import omit from 'lodash-es/omit';
import { useMfaSessionFilterOptionsQuery } from 'src/apps/mfa/hooks/useMfaSessionFilterOptionsQuery';
import { useMfaSingleSessionFilters } from 'src/apps/mfa/hooks/useMfaSingleSessionFilters';
import { TMfaSessionIssuesFilterOptions } from 'src/apps/mfa/services/MfaDashboardServiceTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { flattenObject } from 'src/common/helpers/ObjectsHelper';

import './MfaSingleSessionFiltersSection.scss';

interface IMfaSingleSessionFiltersProps {
	sessionId: string;
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	tableId: string;
	ariaLabel: string;
}

export const MfaSingleSessionFiltersSection: FC<IMfaSingleSessionFiltersProps> = ({
	sessionId,
	filters,
	onFilterChange,
	tableId,
	ariaLabel
}) => {
	const filterOptionsQuery = useMfaSessionFilterOptionsQuery(sessionId);

	const { filterDefinitions, mergedFilterOptions } = useMfaSingleSessionFilters({
		allLabels: filterOptionsQuery.data?.scans ?? [],
		selectedLabels: ((filters as TMfaSessionIssuesFilterOptions)?.scans as OptionType[]) ?? [],
		filterOptions: filterOptionsQuery.data ?? {}
	});

	const initialValues = useMemo(
		() => ({
			text: { searchValue: filters?.searchValue },
			select: omit(filters, 'searchValue')
		}),
		[filters]
	);

	const handleApply = (appliedFilters: IFilterValues): void => {
		const flatFilters = flattenObject<IFilterOptions>(appliedFilters);
		onFilterChange(tableId, flatFilters);
	};

	return (
		<EvSection
			ariaLabel={ariaLabel}
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
			skipLinkId="filters-section"
			dataAutomationId={MFA_SESSION_FILTERS_SECTION_ID}
			className="mfa-single-session-filters-section"
		>
			<EvFilters
				className="data-filters"
				onApply={handleApply}
				onReset={handleApply}
				initialValues={initialValues}
				filterOptions={mergedFilterOptions}
				isFilterOptionsLoaded={!filterOptionsQuery.isLoading}
				filterDefinitions={filterDefinitions}
				shouldSyncValuesWithAvailableOptions={false}
			/>
		</EvSection>
	);
};
